import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as DeleteIcon } from '../../../../../assets/icons/delete-icon.svg';
import DefaultStationImage from '../../../../../assets/images/default-station.png';
import { useAppDispatch } from '../../../../../redux/hooks';
import { setNotificationMessage } from '../../../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../../../shared-components/button/Button';
import ModalBox from '../../../../../shared-components/modal-box/ModalBox';
import { permissionCheck } from '../../../../../shared-functions/JWTDocodeFunction';
import chargingStation from '../../../../../utils/api/partner/station';

interface StationImagesModalProperties {
  isOpen: boolean;
  onClose: () => void;
  stationDetails: any;
  onSuccess: () => void;
}

/**
 * Extract filename from a URL
 * @param {string} url - The URL string
 * @returns {string} The filename
 */
const getFileNameFromUrl = (url: string) => {
  if (!url) return '';
  const decoded = decodeURIComponent(url);
  const parts = decoded.split('/');
  const lastPart = parts[parts.length - 1];
  return lastPart.split('?')[0];
};

/**
 * Helper to append mandatory station details to FormData for editStation API call
 * @param {FormData} formData - The FormData object
 * @param {any} stationDetailsData - The current station details object
 */
const appendStationDataToFormData = (formData: FormData, stationDetailsData: any) => {
  if (!stationDetailsData) return;
  if (stationDetailsData.name) {
    formData.append('name', stationDetailsData.name);
  }
  if (stationDetailsData.latitude !== undefined && stationDetailsData.latitude !== null) {
    formData.append('latitude', String(stationDetailsData.latitude));
  }
  if (stationDetailsData.longitude !== undefined && stationDetailsData.longitude !== null) {
    formData.append('longitude', String(stationDetailsData.longitude));
  }
  if (stationDetailsData.address) {
    formData.append('address', stationDetailsData.address);
  }
  const timeZone = stationDetailsData.time_zone || stationDetailsData.timeZone;
  if (timeZone) {
    formData.append('time_zone', timeZone);
  }

  if (stationDetailsData.operatingHours) {
    const days = [
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
      'sunday',
    ] as const;

    days.forEach((day) => {
      const dayData = stationDetailsData.operatingHours?.[day];
      if (dayData) {
        const isOpen = dayData.isOpen || dayData.status === 'open';
        const openTime = dayData.from || dayData.open || '00:00';
        const closeTime = dayData.to || dayData.close || '23:59';

        if (isOpen) {
          formData.append(`operatingHours[${day}][open]`, openTime);
          formData.append(`operatingHours[${day}][close]`, closeTime);
          formData.append(`operatingHours[${day}][status]`, 'open');
        } else {
          formData.append(`operatingHours[${day}][status]`, 'closed');
        }
      }
    });
    if (stationDetailsData.operatingHours.allDay !== undefined) {
      formData.append('operatingHours[allDay]', String(stationDetailsData.operatingHours.allDay));
    }
  }
};

/**
 * Reusable modal component for editing and uploading station images
 * @param {StationImagesModalProperties} properties - Component properties
 * @returns {React.JSX.Element} The rendered React component
 */
function StationImagesModal(properties: StationImagesModalProperties) {
  const { isOpen, onClose, stationDetails, onSuccess } = properties;
  const { t } = useTranslation('sidePanel');
  const dispatch = useAppDispatch();

  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [removedUrls, setRemovedUrls] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  /**
   * Handle image file upload selection and validation
   * @param {any} event_ - The file input change event
   */
  const handleFileChange = (event_: React.ChangeEvent<HTMLInputElement>) => {
    if (!event_.target.files) return;
    const filesArray = Array.from(event_.target.files);
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    const maxSizeBytes = 1 * 1024 * 1024; // 1MB

    const existingMedias = stationDetails?.medias || [];
    const currentCount =
      existingMedias.filter((m: any) => !removedUrls.includes(m.url)).length + newFiles.length;
    const spaceLeft = 10 - currentCount;

    if (spaceLeft <= 0) {
      dispatch(
        setNotificationMessage({
          status: true,
          message: t('maxImagesLimit', 'You can upload a maximum of 10 images.'),
          type: 'error',
        }),
      );
      return;
    }

    const validFiles: File[] = [];
    let hasInvalidType = false;
    let hasInvalidSize = false;

    filesArray.forEach((file) => {
      if (!allowedTypes.includes(file.type)) {
        hasInvalidType = true;
      } else if (file.size > maxSizeBytes) {
        hasInvalidSize = true;
      } else {
        validFiles.push(file);
      }
    });

    if (hasInvalidType) {
      dispatch(
        setNotificationMessage({
          status: true,
          message: t('invalidFileType', 'Only JPG and PNG files are allowed.'),
          type: 'error',
        }),
      );
    }

    if (hasInvalidSize) {
      dispatch(
        setNotificationMessage({
          status: true,
          message: t('invalidFileSize', 'Files must be smaller than 1MB.'),
          type: 'error',
        }),
      );
    }

    if (validFiles.length > 0) {
      const filesToAdd = validFiles.slice(0, spaceLeft);
      setNewFiles((previous) => [...previous, ...filesToAdd]);
      if (validFiles.length > spaceLeft) {
        dispatch(
          setNotificationMessage({
            status: true,
            message: t(
              'maxImagesLimitIgnored',
              'Some files were ignored because you can upload a maximum of 10 images.',
            ),
            type: 'error',
          }),
        );
      }
    }

    // eslint-disable-next-line no-param-reassign
    event_.target.value = '';
  };

  /**
   * Remove an existing image URL from view and mark it for removal
   * @param {string} url - The URL of the image to remove
   */
  const handleRemoveExistingMedia = (url: string) => {
    setRemovedUrls((previous) => [...previous, url]);
  };

  /**
   * Remove a newly added image from the upload list
   * @param {number} index - The index of the file to remove
   */
  const handleRemoveNewFile = (index: number) => {
    setNewFiles((previous) => previous.filter((_, index_) => index_ !== index));
  };

  /**
   * Close the edit modal and discard unsaved selections
   */
  const handleCloseEditModal = () => {
    onClose();
    setNewFiles([]);
    setRemovedUrls([]);
    setSaveError(null);
  };

  /**
   * Save the image additions and removals by invoking the API
   */
  const handleSave = async () => {
    if (!stationDetails?._id) return;

    // Check if any new file exceeds 900KB
    const maxSizeBytes = 900 * 1024;
    const hasLargeFile = newFiles.some((file) => file.size > maxSizeBytes);
    if (hasLargeFile) {
      const errorMessage = t('invalidFileSize', 'Files must be smaller than 900KB.');
      setSaveError(errorMessage);
      dispatch(
        setNotificationMessage({
          status: true,
          message: errorMessage,
          type: 'error',
        }),
      );
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    const formData = new FormData();
    newFiles.forEach((file) => {
      formData.append('medias', file);
    });
    removedUrls.forEach((url, index) => {
      formData.append(`removedUrls[${index}]`, url);
    });

    appendStationDataToFormData(formData, stationDetails);

    try {
      await chargingStation.editStation(stationDetails._id, formData, () => {});

      dispatch(
        setNotificationMessage({
          status: true,
          message: t('imagesUpdatedSuccessfully', 'Station images updated successfully.'),
          type: 'success',
        }),
      );

      onSuccess();
      handleCloseEditModal();
    } catch (error: any) {
      console.error('Failed to save station images:', error);
      let errorMessage = '';
      const isNetworkError =
        error?.message === 'Failed to fetch' ||
        error?.message?.includes('fetch') ||
        error?.message === 'Network Error';
      if (error?.response?.status === 413 || (isNetworkError && newFiles.length > 0)) {
        errorMessage = t(
          'requestEntityTooLarge',
          'Request Entity Too Large: The uploaded files exceed the allowed limit.',
        );
      } else {
        errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          t('failedToUpdateImages', 'Failed to update station images.');
      }
      setSaveError(errorMessage);
      dispatch(
        setNotificationMessage({
          status: true,
          message: errorMessage,
          type: 'error',
        }),
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalBox
      open={isOpen}
      title={t('stationImagesTitle', 'Station Images')}
      closeModal={handleCloseEditModal}
      header
      position="center"
      content={
        <div className="station-images-modal-body">
          <p className="modal-subtitle">
            {t(
              'uploadMultipleImagesDesc',
              'Upload multiple images of your charging station. You can add up to 10 images.',
            )}
          </p>

          <div className="images-grid">
            {/* Existing Medias */}
            {(stationDetails?.medias || []).map((media: any, index: number) => {
              const url = media.signedUrl || media.url;
              const isRemoved = removedUrls.includes(media.url);
              if (isRemoved) return null;
              return (
                <div className="modal-image-card" key={media._id || index}>
                  <div className="image-preview-wrapper">
                    <img
                      src={url}
                      alt=""
                      onError={(event_) => {
                        const img = event_.currentTarget as HTMLImageElement;
                        img.src = DefaultStationImage;
                      }}
                    />
                    {(permissionCheck('partnerChargingStationDelete') ||
                      permissionCheck('cpoChargingStationDelete')) && (
                      <button
                        type="button"
                        className="remove-image-overlay-btn"
                        onClick={() => handleRemoveExistingMedia(media.url)}>
                        <DeleteIcon />
                      </button>
                    )}
                  </div>
                  <div className="image-filename-label">{getFileNameFromUrl(url)}</div>
                </div>
              );
            })}

            {/* New files preview */}
            {newFiles.map((file, index) => {
              const objectUrl = URL.createObjectURL(file);
              return (
                <div className="modal-image-card" key={index}>
                  <div className="image-preview-wrapper">
                    <img src={objectUrl} alt="" />
                    <button
                      type="button"
                      className="remove-image-overlay-btn"
                      onClick={() => handleRemoveNewFile(index)}>
                      <DeleteIcon />
                    </button>
                  </div>
                  <div className="image-filename-label">{file.name}</div>
                </div>
              );
            })}

            {/* Add More dashed button */}
            {(stationDetails?.medias || []).filter((m: any) => !removedUrls.includes(m.url)).length +
              newFiles.length <
              10 && (
              <label className="modal-add-more-box-btn" htmlFor="station-image-upload-input">
                <input
                  id="station-image-upload-input"
                  type="file"
                  multiple
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
                <div className="dashed-box-content">
                  <span className="plus-icon">+</span>
                  <span className="box-title">{t('addMoreImages', 'Add More Images')}</span>
                  <span className="box-desc">{t('fileRequirements', 'JPG, PNG up to 1MB')}</span>
                </div>
              </label>
            )}
          </div>

          {saveError && (
            <div className="modal-error-message">
              <span className="error-icon">⚠️</span>
              <span className="error-text">{saveError}</span>
            </div>
          )}

          <div className="modal-actions-container">
            <MyButton
              label={t('cancel')}
              buttonType="primary-outline"
              onClickFunc={handleCloseEditModal}
            />
            <MyButton
              label={isSaving ? t('saving', 'Saving...') : t('save')}
              buttonType="submit"
              onClickFunc={handleSave}
              disableButton={
                isSaving ||
                (
                  !permissionCheck('partnerChargingStationUpdate') &&
                  !permissionCheck('cpoChargingStationUpdate'))
              }
              loading={isSaving}
            />
          </div>
        </div>
      }
    />
  );
}

export default StationImagesModal;
